import React, { useState, useEffect } from 'react';
import api from '../../services/api';

function MessagePreviewTab({ practiceId }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [patientSource, setPatientSource] = useState('sample');
  const [sampleData, setSampleData] = useState({ first_name: 'Jane', last_name: 'Doe' });
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searching, setSearching] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (practiceId) {
      api.getTemplates(practiceId).then(res => {
        setTemplates(res.data || []);
      }).catch(() => {});
    }
  }, [practiceId]);

  const handleSearchPatients = async () => {
    if (!patientSearch.trim()) return;
    setSearching(true);
    try {
      const res = await api.getPatients(practiceId, { search: patientSearch.trim(), limit: 10 });
      setPatients(res.data || []);
    } catch (err) {
      setPatients([]);
    } finally {
      setSearching(false);
    }
  };

  const handlePatientSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearchPatients();
  };

  const handlePreview = async () => {
    if (!selectedTemplateId) return;
    setLoading(true);
    setError(null);
    setPreviewResult(null);

    try {
      const body = {};
      if (patientSource === 'sample') {
        body.variables = { ...sampleData };
      } else if (selectedPatient) {
        body.patient_id = selectedPatient.id;
      } else {
        setError('Select a patient first');
        setLoading(false);
        return;
      }

      const res = await api.previewTemplate(selectedTemplateId, body);
      if (res.success) {
        setPreviewResult(res.data);
      } else {
        setError(res.error || 'Preview failed');
      }
    } catch (err) {
      setError(err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === parseInt(selectedTemplateId));

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Template Selection</h3>

        <div style={styles.formGroup}>
          <label style={styles.label}>Template</label>
          <select
            style={styles.select}
            value={selectedTemplateId}
            onChange={(e) => { setSelectedTemplateId(e.target.value); setPreviewResult(null); }}
          >
            <option value="">Select a template...</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>
                {t.template_name || t.campaign_type} ({t.channel})
              </option>
            ))}
          </select>
        </div>

        {selectedTemplate && (
          <div style={styles.channelBadge}>
            Channel: <strong>{selectedTemplate.channel?.toUpperCase()}</strong>
            {selectedTemplate.campaign_type && (
              <span style={{ marginLeft: '12px', color: '#6b7280' }}>
                Campaign: {selectedTemplate.campaign_type}
              </span>
            )}
          </div>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Patient Data</h3>

        <div style={styles.radioGroup}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="patientSource"
              value="sample"
              checked={patientSource === 'sample'}
              onChange={() => setPatientSource('sample')}
            />
            Sample Data
          </label>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="patientSource"
              value="search"
              checked={patientSource === 'search'}
              onChange={() => setPatientSource('search')}
            />
            Search Patient
          </label>
        </div>

        {patientSource === 'sample' && (
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>First Name</label>
              <input
                type="text"
                style={styles.input}
                value={sampleData.first_name}
                onChange={(e) => setSampleData({ ...sampleData, first_name: e.target.value })}
                placeholder="Jane"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Last Name</label>
              <input
                type="text"
                style={styles.input}
                value={sampleData.last_name}
                onChange={(e) => setSampleData({ ...sampleData, last_name: e.target.value })}
                placeholder="Doe"
              />
            </div>
          </div>
        )}

        {patientSource === 'search' && (
          <div>
            <div style={styles.searchRow}>
              <input
                type="text"
                style={styles.input}
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                onKeyDown={handlePatientSearchKeyDown}
                placeholder="Search by name or phone..."
              />
              <button style={styles.searchButton} onClick={handleSearchPatients} disabled={searching}>
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {patients.length > 0 && (
              <div style={styles.patientList}>
                {patients.map(p => (
                  <div
                    key={p.id}
                    style={{
                      ...styles.patientItem,
                      backgroundColor: selectedPatient?.id === p.id ? '#eff6ff' : '#fff',
                      borderColor: selectedPatient?.id === p.id ? '#3b82f6' : '#e5e7eb',
                    }}
                    onClick={() => setSelectedPatient(p)}
                  >
                    <span style={styles.patientName}>{p.first_name} {p.last_name}</span>
                    <span style={styles.patientPhone}>{p.phone}</span>
                  </div>
                ))}
              </div>
            )}

            {selectedPatient && (
              <div style={styles.selectedPatientBadge}>
                Selected: <strong>{selectedPatient.first_name} {selectedPatient.last_name}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      <button
        style={{
          ...styles.previewButton,
          opacity: !selectedTemplateId || loading ? 0.6 : 1,
        }}
        onClick={handlePreview}
        disabled={!selectedTemplateId || loading}
      >
        {loading ? 'Generating Preview...' : 'Preview Message'}
      </button>

      {error && (
        <div style={styles.errorBox}>{error}</div>
      )}

      {previewResult && (
        <div style={styles.resultCard}>
          <h3 style={styles.cardTitle}>Preview Result</h3>

          <div style={styles.statsRow}>
            <div style={styles.stat}>
              <span style={styles.statLabel}>Channel</span>
              <span style={styles.statValue}>{previewResult.channel?.toUpperCase()}</span>
            </div>
            <div style={styles.stat}>
              <span style={styles.statLabel}>Characters</span>
              <span style={styles.statValue}>{previewResult.character_count}</span>
            </div>
            {previewResult.channel === 'sms' && (
              <div style={styles.stat}>
                <span style={styles.statLabel}>SMS Segments</span>
                <span style={styles.statValue}>{calcSmsSegments(previewResult.character_count)}</span>
              </div>
            )}
          </div>

          {previewResult.rendered_subject && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Subject</label>
              <div style={styles.previewBox}>{previewResult.rendered_subject}</div>
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>{previewResult.channel === 'email' ? 'Body' : 'Message'}</label>
            <div style={styles.previewBox}>{previewResult.rendered_content}</div>
          </div>

          {previewResult.unresolved_variables?.length > 0 && (
            <div style={styles.warningBox}>
              Unresolved variables: {previewResult.unresolved_variables.map(v => `{{${v}}}`).join(', ')}
            </div>
          )}

          {previewResult.unresolved_variables?.length === 0 && (
            <div style={styles.successBox}>All variables resolved successfully</div>
          )}
        </div>
      )}
    </div>
  );
}

function calcSmsSegments(charCount) {
  if (!charCount || charCount <= 0) return 0;
  if (charCount <= 160) return 1;
  return Math.ceil(charCount / 153);
}

const styles = {
  container: {
    padding: '20px 0',
    overflowY: 'auto',
    height: '100%',
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
  },
  resultCard: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '16px',
  },
  cardTitle: {
    margin: '0 0 16px',
    fontSize: '15px',
    fontWeight: 600,
    color: '#111827',
  },
  formGroup: {
    marginBottom: '12px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '4px',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#111827',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  channelBadge: {
    fontSize: '13px',
    color: '#374151',
    padding: '8px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    marginTop: '8px',
  },
  radioGroup: {
    display: 'flex',
    gap: '20px',
    marginBottom: '16px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#374151',
    cursor: 'pointer',
  },
  searchRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  searchButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  patientList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '200px',
    overflowY: 'auto',
    marginBottom: '12px',
  },
  patientItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  patientName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#111827',
  },
  patientPhone: {
    fontSize: '13px',
    color: '#6b7280',
  },
  selectedPatientBadge: {
    fontSize: '13px',
    color: '#1e40af',
    padding: '6px 12px',
    backgroundColor: '#eff6ff',
    borderRadius: '6px',
  },
  previewButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  errorBox: {
    marginTop: '12px',
    padding: '10px 14px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '6px',
    fontSize: '13px',
    border: '1px solid #fecaca',
  },
  statsRow: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  statLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statValue: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
  previewBox: {
    padding: '14px 16px',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#111827',
    whiteSpace: 'pre-wrap',
  },
  warningBox: {
    padding: '10px 14px',
    backgroundColor: '#fffbeb',
    color: '#92400e',
    borderRadius: '6px',
    fontSize: '13px',
    border: '1px solid #fde68a',
  },
  successBox: {
    padding: '10px 14px',
    backgroundColor: '#f0fdf4',
    color: '#166534',
    borderRadius: '6px',
    fontSize: '13px',
    border: '1px solid #bbf7d0',
  },
};

export default MessagePreviewTab;
